import { ref, computed, watch } from "vue";
import { useI18n } from "vue-i18n";
import Fuse from "fuse.js";
import { blogFiles } from "@/data/siteContent";
import {
  extractBlogTitle,
  parseFrontmatter,
  slugFromFilename,
  stripMarkdown,
} from "@/utils/blog";
import { useSiteContent } from "@/composables/useSiteContent";

export interface SearchResult {
  id: string;
  title: string;
  excerpt: string;
  category: "Page" | "Profile" | "Blog" | "Project" | "Resume";
  route: string;
  anchor?: string;
}

interface SearchDocument {
  id: string;
  title: string;
  content: string;
  category: SearchResult["category"];
  route: string;
  anchor?: string;
}

const documents = ref<SearchDocument[]>([]);
const loading = ref(false);
const ready = ref(false);

let fuse: Fuse<SearchDocument> | null = null;

export const useSearchIndex = () => {
  const { t, locale } = useI18n();
  const { profile, education, skills, workExperience, projects, navPages, cvSummary } =
    useSiteContent();

  const query = ref("");

  const buildStaticDocuments = (): SearchDocument[] => {
    const docs: SearchDocument[] = [];

    navPages.value.forEach((page) => {
      docs.push({
        id: `page-${page.name}`,
        title: t(`nav.${page.name}`),
        content: page.description,
        category: "Page",
        route: page.route,
      });
    });

    docs.push({
      id: "profile-intro",
      title: t("site.search.introTitle", { name: profile.value.name }),
      content: `${profile.value.name} ${profile.value.username} ${profile.value.tagline}`,
      category: "Profile",
      route: "/Profile",
      anchor: "introduction",
    });

    docs.push({
      id: "profile-education",
      title: t("site.search.educationTitle"),
      content: `${education.value.institution} ${education.value.years} ${education.value.degree}`,
      category: "Profile",
      route: "/Profile",
      anchor: "education",
    });

    const skillText = Object.entries(skills.value)
      .map(([group, items]) => `${group}: ${items.join(" ")}`)
      .join(" ");
    docs.push({
      id: "profile-skills",
      title: t("site.search.skillsTitle"),
      content: skillText,
      category: "Profile",
      route: "/Profile",
      anchor: "skills",
    });

    workExperience.value.forEach((exp, i) => {
      docs.push({
        id: `work-${i}`,
        title: `${exp.title} — ${exp.position}`,
        content: [exp.duration, ...exp.description].join(" "),
        category: "Profile",
        route: "/Profile",
        anchor: "workExperience",
      });
    });

    projects.value.forEach((project, i) => {
      docs.push({
        id: `project-${i}`,
        title: project.title,
        content: `${project.category} ${project.blurb} ${project.link}`,
        category: "Project",
        route: "/Project",
      });
    });

    docs.push({
      id: "resume-cv",
      title: t("site.search.resumeTitle"),
      content: cvSummary.value,
      category: "Resume",
      route: "/Resume",
    });

    return docs;
  };

  const loadBlogDocuments = async (): Promise<SearchDocument[]> => {
    const blogDocs = await Promise.all(
      blogFiles.map(async (filename): Promise<SearchDocument | null> => {
        try {
          const response = await fetch(`/blog/${filename}`);
          if (!response.ok) return null;
          const text = await response.text();
          const { frontmatter, body } = parseFrontmatter(text);
          const title = extractBlogTitle(body, filename);
          const slug = slugFromFilename(filename);
          const content = frontmatter.summary || stripMarkdown(body);
          return {
            id: `blog-${filename}`,
            title,
            content,
            category: "Blog",
            route: `/Blog/${slug}`,
          };
        } catch {
          return null;
        }
      }),
    );
    return blogDocs.filter((doc): doc is SearchDocument => doc !== null);
  };

  const rebuildIndex = async (): Promise<void> => {
    loading.value = true;
    ready.value = false;
    fuse = null;
    try {
      const staticDocs = buildStaticDocuments();
      const blogDocs = await loadBlogDocuments();
      documents.value = [...staticDocs, ...blogDocs];
      fuse = new Fuse(documents.value, {
        keys: [
          { name: "title", weight: 0.45 },
          { name: "content", weight: 0.35 },
          { name: "category", weight: 0.2 },
        ],
        threshold: 0.4,
        includeScore: true,
        minMatchCharLength: 2,
      });
      ready.value = true;
    } finally {
      loading.value = false;
    }
  };

  const initSearchIndex = async (): Promise<void> => {
    if (ready.value || loading.value) return;
    await rebuildIndex();
  };

  watch(locale, () => {
    if (ready.value) rebuildIndex();
  });

  const results = computed<SearchResult[]>(() => {
    if (!query.value.trim() || !fuse) return [];
    const hits = fuse.search(query.value.trim(), { limit: 12 });
    return hits.map((hit) => ({
      id: hit.item.id,
      title: hit.item.title,
      excerpt:
        hit.item.content.slice(0, 120) +
        (hit.item.content.length > 120 ? "…" : ""),
      category: hit.item.category,
      route: hit.item.route,
      anchor: hit.item.anchor,
    }));
  });

  return {
    query,
    results,
    loading,
    ready,
    initSearchIndex,
  };
};
